import {
  FormControl,
  FormLabel,
  Flex,
  FormErrorMessage,
  Collapse,
  Box,
  Text,
  Link,
  Select,
  Tooltip,
  Input,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import { useContext, useEffect, useRef, useMemo, useState } from 'react';
import {
  removeField,
  setFieldProp,
  setLoyaltyBalance,
  setSimpleField,
  clearStampShortValues,
  clearStampLongValues,
  setFocusedField,
} from '../../../store/redux/slices/template.slice';
import {
  clearError,
  clearNamespaceErrors,
} from '../../../store/redux/slices/templateErrors.slice';
import AddonInput from './AddonInput';
import { labelToFieldId } from './utils/formatTemplatePayload';
import SwitchField from './SwitchField';
import BuilderSwitch from './BuilderSwitch';
import OverrideFieldValueRadio from './OverrideFieldValueRadio';
import { LabelWithTooltip } from './LabelWithTooltip';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import { CustomEditIcon } from '../../../theme/icons/CustomEditIcon';
import DragDotsIcon from '../../../theme/icons/DragDotsIcon';
import TextLinkToggle from './TextLinkToggle';
import CharLimitWarning from './CharLimitWarning';
import { MeContext } from '../../../context/MeContext';
import { LOYALTY_BALANCE_ERROR_MESSAGE } from './utils/fieldHelpers';

const ANCHOR_REGEX = /^<a\s+href=[''""'"](.*?)[''""'"]\s*>(.*?)<\/a>$/;

function parseAnchorTag(value: string): { linkText: string; linkUrl: string } {
  const match = value.match(ANCHOR_REGEX);
  if (match) return { linkUrl: match[1], linkText: match[2] };
  return { linkText: value, linkUrl: '' };
}

function buildAnchorTag(linkText: string, linkUrl: string): string {
  if (!linkUrl) return linkText;
  return `<a href="${linkUrl}">${linkText}</a>`;
}

const TemplateContentField = ({
  label,
  section = 'header',
  slot = 'one',
  newlyAdded = false,
  validateField,
  errors,
  isMinRequiredReached,
  isShared = true,
  isEdit,
  autoGenerateFieldId = false,
  isDraggable = false,
  dragHandleProps,
  controlledOpen,
  onOpenChange,
  showLinkToggle = false,
}: {
  label: string;
  section?: 'header' | 'central' | 'details' | 'back';
  slot?: string;
  newlyAdded?: boolean;
  validateField: (fieldPath: string, value: any, context?: any) => void;
  errors: Record<string, string>;
  isMinRequiredReached: boolean;
  isShared?: boolean;
  isEdit?: boolean;
  autoGenerateFieldId?: boolean;
  isDraggable?: boolean;
  dragHandleProps?: any;
  controlledOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  showLinkToggle?: boolean;
}) => {
  const { handleFocus } = useTemplateFieldValidation();
  const meCtx = useContext(MeContext) as
    | { state: Record<string, unknown> }
    | undefined;
  const expressivePassRolloutEnabled =
    meCtx?.state?.expressivePassRolloutEnabled === true;
  const dispatch = useAppDispatch();
  const [localIsOpen, setLocalIsOpen] = useState(
    autoGenerateFieldId && !isEdit
  );
  const isOpen = controlledOpen !== undefined ? controlledOpen : localIsOpen;
  const setIsOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    } else {
      setLocalIsOpen(value);
    }
  };
  const [isIdEditing, setIsIdEditing] = useState(false);
  const [isIdManuallyEdited, setIsIdManuallyEdited] = useState(
    isEdit === true && !newlyAdded
  );
  const [overrideFieldValue, setOverrideFieldValue] = useState<'new' | 'all'>(
    'new'
  );
  const [overrideFieldValueOpen, setOverrideFieldValueOpen] = useState(false);
  const templateType = useAppSelector((s) => s.template.templateType);
  const stampFeedbackTypes = useAppSelector(
    (s) => s.template.stampFeedbackTypes
  );
  const stampShortKey = useAppSelector((s) => s.template.stampShortKey);
  const stampLongKey = useAppSelector((s) => s.template.stampLongKey);
  const fields = useAppSelector((s) => s.template.fields);
  const originalDefaultValues = useAppSelector(
    (s) => s.template.originalDefaultValues
  );
  const field = fields[section][slot] ?? {
    id: '',
    label: '',
    defaultValue: '',
    isStampCardField: null,
    _isLink: undefined,
  };

  const loyaltyBalanceEnabledRef = useRef<HTMLDivElement>(null);
  const idFieldRef = useRef<HTMLDivElement>(null);
  const labelFieldRef = useRef<HTMLDivElement>(null);
  const defaultValueFieldRef = useRef<HTMLDivElement>(null);
  const loyaltyBalanceNumberTypeRef = useRef<HTMLDivElement>(null);
  const loyaltyBalanceBalanceRef = useRef<HTMLDivElement>(null);
  const isStampCardFieldRef = useRef<HTMLDivElement>(null);

  const fieldBase = `${section}.${slot}`;
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      [`${fieldBase}.id`]: idFieldRef,
      [`${fieldBase}.label`]: labelFieldRef,
      [`${fieldBase}.defaultValue`]: defaultValueFieldRef,
      'loyaltyBalance.enabled': loyaltyBalanceEnabledRef,
      'loyaltyBalance.numberType': loyaltyBalanceNumberTypeRef,
      'loyaltyBalance.balance': loyaltyBalanceBalanceRef,
      isStampCardField: isStampCardFieldRef,
    },
  });

  const handleChange = (field: string, value: string) => {
    const currentField = fields[section][slot] ?? {
      id: '',
      label: '',
      defaultValue: '',
      isStampCardField: null,
    };
    const nextFields = {
      ...fields,
      [section]: {
        ...fields[section],
        [slot]: { ...currentField, [field]: value },
      },
    };
    validateField(`${section}.${slot}.${field}`, value, { fields: nextFields });
    dispatch(setFieldProp({ section, slot, key: field as any, value }));

    // Auto-generate ID from label as user types
    if (
      autoGenerateFieldId &&
      field === 'label' &&
      !isIdEditing &&
      !isIdManuallyEdited
    ) {
      const generatedId = labelToFieldId(value, section, slot);
      dispatch(setFieldProp({ section, slot, key: 'id', value: generatedId }));
    }

    // Stamp keys are stored by section_slot; no need to update when id changes
  };

  const focusedField = useAppSelector((s) => s.template.focusedField);
  const idPath = `fields.${section}.${slot}.id`;
  const labelPath = `fields.${section}.${slot}.label`;
  const defaultValuePath = `fields.${section}.${slot}.defaultValue`;

  const loyaltyBalance = useAppSelector((s) => s.template.loyaltyBalance);
  const templateUuid = useAppSelector((s) => s.template.uuid);

  const isLoyaltyBalanceField =
    section === 'header' && slot === 'one' && loyaltyBalance.enabled;
  const hasNonNumericValue =
    isLoyaltyBalanceField &&
    String(field.defaultValue ?? '') !== '' &&
    isNaN(Number(field.defaultValue));

  useEffect(() => {
    if (!isLoyaltyBalanceField) return;
    const current = String(field.defaultValue ?? '');
    if (current === '') return;
    const num =
      loyaltyBalance.numberType === 'decimal'
        ? parseFloat(current)
        : parseInt(current, 10);
    if (isNaN(num)) return;
    const formatted =
      loyaltyBalance.numberType === 'decimal' ? num.toFixed(2) : String(num);
    if (formatted !== current) {
      dispatch(
        setFieldProp({ section, slot, key: 'defaultValue', value: formatted })
      );
    }
  }, [loyaltyBalance.numberType]);

  const isLink =
    showLinkToggle &&
    (field._isLink ?? ANCHOR_REGEX.test(String(field.defaultValue ?? '')));

  const savedLinkUrlRef = useRef<string>('');

  const handleLinkToggle = (linkMode: boolean) => {
    if (!linkMode && ANCHOR_REGEX.test(field.defaultValue ?? '')) {
      const { linkText, linkUrl } = parseAnchorTag(field.defaultValue ?? '');
      savedLinkUrlRef.current = linkUrl;
      handleChange('defaultValue', linkText || linkUrl);
    } else if (linkMode) {
      const text = field.defaultValue ?? '';
      if (ANCHOR_REGEX.test(text)) {
        // Already an anchor tag — keep as-is, link fields will parse it
      } else if (savedLinkUrlRef.current) {
        const linkText = text === savedLinkUrlRef.current ? '' : text;
        handleChange(
          'defaultValue',
          buildAnchorTag(linkText, savedLinkUrlRef.current)
        );
        savedLinkUrlRef.current = '';
      } else if (/^(https?:\/\/|www\.|mailto:|tel:)/i.test(text)) {
        handleChange('defaultValue', buildAnchorTag('', text));
      }
    }
    dispatch(setFieldProp({ section, slot, key: '_isLink', value: linkMode }));
  };

  const handleLinkFieldChange = (
    part: 'linkText' | 'linkUrl',
    value: string
  ) => {
    const dv = field.defaultValue ?? '';
    const parsed = parseAnchorTag(dv);
    const updated = { ...parsed, [part]: value };
    const newDefaultValue = buildAnchorTag(updated.linkText, updated.linkUrl);
    handleChange('defaultValue', newDefaultValue);
  };

  useEffect(() => {
    if (newlyAdded) {
      setIsOpen(true);
      dispatch(
        setFieldProp({ section, slot, key: '_newlyAdded', value: false })
      );
    }
  }, [newlyAdded]);

  const fieldHasErrors = useMemo(() => {
    const errorKeys = Object.keys(errors);
    const fieldBase = `fields.${section}.${slot}`;
    return (
      errorKeys.some((key) => key.startsWith(`${fieldBase}.`)) ||
      (section === 'header' &&
        (errorKeys.includes('loyaltyBalance.enabled') ||
          errorKeys.includes('loyaltyBalance.numberType') ||
          errorKeys.includes('loyaltyBalance.defaultValue')))
    );
  }, [errors, section, slot]);

  const prevTemplateUuidRef = useRef(templateUuid);
  useEffect(() => {
    if (prevTemplateUuidRef.current !== templateUuid) {
      prevTemplateUuidRef.current = templateUuid;
      setIsOpen(false);
    }
  }, [templateUuid]);

  useEffect(() => {
    if (autoGenerateFieldId && focusedField === `${section}.${slot}.id`) {
      setIsOpen(true);
      setIsIdEditing(true);
      dispatch(setFocusedField(null));
    }
  }, [focusedField, autoGenerateFieldId, section, slot, dispatch]);

  useEffect(() => {
    if (fieldHasErrors) {
      setIsOpen(true);
    }
  }, [fieldHasErrors]);

  useEffect(() => {
    if (!isEdit) return;

    const originalField = originalDefaultValues[section]?.[slot];
    const originalValue = originalField?.defaultValue ?? '';
    const editedValue = field.defaultValue ?? '';

    // Only show override when changing an existing field's default value,
    // not when adding a new field to the template.
    const fieldExisted = !!originalField?.id;
    const shouldShowOverride =
      fieldExisted && String(originalValue) !== String(editedValue);

    setOverrideFieldValueOpen(shouldShowOverride);

    if (shouldShowOverride) {
      // Restore from Redux state so the selection persists across tab switches
      const currentOverride = (field as any).overrideFieldValue;
      setOverrideFieldValue(currentOverride ? 'all' : 'new');
    } else {
      setOverrideFieldValue('new');
      dispatch(
        setFieldProp({
          section,
          slot,
          key: 'overrideFieldValue',
          value: false,
        })
      );
    }
  }, [
    isEdit,
    field.defaultValue,
    originalDefaultValues,
    section,
    slot,
    dispatch,
  ]);

  const renderEditableId = (showEditIcon: boolean) => (
    <>
      {(field.id || autoGenerateFieldId) && (
        <Flex alignItems="baseline" gap={1.5} flex={1}>
          <Text
            as="span"
            fontSize="16px"
            fontWeight="400"
            fontFamily={
              autoGenerateFieldId ? 'Inter Medium, sans-serif' : undefined
            }
          >
            |
          </Text>
          <Box>
            <Flex alignItems="baseline" gap={1.5}>
              {isIdEditing ? (
                <Input
                  size="xs"
                  fontSize="16px"
                  fontWeight="400"
                  value={field.id || ''}
                  onChange={(e) => {
                    handleChange('id', e.target.value);
                    setIsIdManuallyEdited(true);
                  }}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  onBlur={() => {
                    if (field.id) setIsIdEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && field.id) setIsIdEditing(false);
                  }}
                  autoFocus
                  variant="unstyled"
                  px={1}
                  minW="80px"
                  maxW="200px"
                  borderRadius={0}
                  _focus={{
                    boxShadow: 'none',
                    borderColor:
                      !field.id || errors[idPath] ? '#E53E3E' : 'transparent',
                  }}
                  _focusVisible={{ boxShadow: 'none' }}
                />
              ) : field.id ? (
                <Text fontSize="16px" fontWeight="400">
                  {field.id}
                </Text>
              ) : (
                <Text
                  fontSize="16px"
                  display="inline-block"
                  w="80px"
                  cursor="pointer"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsIdEditing(true);
                  }}
                >
                  &nbsp;
                </Text>
              )}
              {autoGenerateFieldId &&
                showEditIcon &&
                !isIdEditing &&
                field.id && (
                  <Box
                    as="button"
                    type="button"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setIsIdEditing(true);
                    }}
                    color={errors[idPath] ? '#E53E3E' : 'grey3'}
                    _hover={{ opacity: 0.6 }}
                  >
                    <CustomEditIcon boxSize="14px" />
                  </Box>
                )}
            </Flex>
          </Box>
        </Flex>
      )}
    </>
  );

  return (
    <Box
      p={autoGenerateFieldId ? 0 : 3}
      border={autoGenerateFieldId ? 'none' : '1px solid'}
      borderColor="primaryBackground"
      borderRadius="md"
      mb={autoGenerateFieldId ? 0 : 3}
      position="relative"
    >
      <FormControl isRequired={!isMinRequiredReached}>
        {/* Header / Toggle */}
        {showLinkToggle ? (
          <>
            {/* Collapsed: label + id + chevron on one row */}
            {!isOpen && (
              <Flex
                align="center"
                justify="space-between"
                cursor="pointer"
                onClick={() => setIsOpen(true)}
              >
                <Flex alignItems="baseline" gap={2} flex={1}>
                  <FormLabel
                    variant="templateBuilderLabel"
                    m={0}
                    fontWeight="500"
                    fontFamily="Inter Medium, sans-serif"
                  >
                    {label}
                  </FormLabel>
                  {renderEditableId(false)}
                </Flex>
                <ChevronDownIcon boxSize="20px" />
              </Flex>
            )}
            {/* Expanded: chevron on top, then label + id + toggle */}
            {isOpen && (
              <>
                <Flex
                  align="center"
                  justify="flex-end"
                  cursor="pointer"
                  onClick={() => setIsOpen(false)}
                >
                  <ChevronUpIcon boxSize="20px" />
                </Flex>
                <Flex
                  align="center"
                  justify="space-between"
                  cursor="pointer"
                  onClick={() => setIsOpen(false)}
                  mb={1}
                  mt={3}
                >
                  <Flex alignItems="baseline" gap={2} flex={1}>
                    <FormLabel
                      variant="templateBuilderLabel"
                      m={0}
                      fontWeight="500"
                      fontFamily="Inter Medium, sans-serif"
                    >
                      {label}
                    </FormLabel>
                    {renderEditableId(true)}
                  </Flex>
                  <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <TextLinkToggle
                      isLink={isLink}
                      onChange={handleLinkToggle}
                    />
                  </Box>
                </Flex>
              </>
            )}
          </>
        ) : (
          <Flex
            align="center"
            justify="space-between"
            cursor="pointer"
            onClick={() => setIsOpen(!isOpen)}
            mb={autoGenerateFieldId ? 1 : 0}
          >
            <Flex
              alignItems={autoGenerateFieldId ? 'baseline' : 'center'}
              gap={2}
              flex={1}
            >
              <FormLabel
                variant="templateBuilderLabel"
                m={0}
                fontWeight="500"
                fontFamily={
                  autoGenerateFieldId ? 'Inter Medium, sans-serif' : undefined
                }
              >
                {label}
              </FormLabel>
              {renderEditableId(isOpen)}
            </Flex>

            {isOpen ? (
              <ChevronUpIcon boxSize="20px" />
            ) : (
              <ChevronDownIcon boxSize="20px" />
            )}
          </Flex>
        )}
        {autoGenerateFieldId && (!field.id || errors[idPath]) && (
          <Flex
            align="center"
            gap={2}
            mt={2}
            mb={1}
            ml={3}
            color="#E53E3E"
            fontSize="12px"
          >
            <CustomErrorIcon boxSize="12px" />
            {!field.id
              ? 'Field ID is required'
              : 'Field ID name must be unique'}
          </Flex>
        )}
        {/* Form Fields (collapsible) */}
        <Collapse in={isShared ? isOpen : true} animateOpacity>
          {/* LOYALTY BALANCE (rendered in FrontFields when autoGenerateFieldId is on) */}
          {!autoGenerateFieldId &&
            section === 'header' &&
            templateType !== 'stampCard' && (
              <Box mt={4}>
                <FieldEditIndicator
                  fieldRef={loyaltyBalanceEnabledRef}
                  isFieldBeingEdited={isFieldBeingEdited}
                  activeFieldRef={activeFieldRef}
                  left="-55px"
                  top="55px"
                >
                  <FormControl
                    mt={6}
                    mb={loyaltyBalance.enabled ? 4 : 6}
                    isInvalid={
                      !!errors['loyaltyBalance.enabled'] ||
                      !!errors['loyaltyBalance.numberType']
                    }
                  >
                    <SwitchField
                      value={loyaltyBalance.enabled}
                      onChange={(value) => {
                        dispatch(setLoyaltyBalance({ enabled: value }));
                        handleFocus('loyaltyBalance.enabled');
                      }}
                      label={
                        <LabelWithTooltip
                          label="Enable loyalty balance"
                          tooltip="Converts your header field into a balance field that can be used to display and update a customer's loyalty balance."
                        />
                      }
                    />
                  </FormControl>
                </FieldEditIndicator>
                <Collapse in={loyaltyBalance.enabled}>
                  <FieldEditIndicator
                    fieldRef={loyaltyBalanceNumberTypeRef}
                    isFieldBeingEdited={isFieldBeingEdited}
                    activeFieldRef={activeFieldRef}
                    left="-55px"
                    top="60px"
                  >
                    <FormControl
                      isInvalid={!!errors['loyaltyBalance.numberType']}
                    >
                      <LabelWithTooltip
                        label="Balance number type"
                        tooltip="Select the number type for the balance field. This determines the format of the balance value displayed on the pass."
                      />
                      <Select
                        value={loyaltyBalance.numberType}
                        onChange={(e) =>
                          dispatch(
                            setLoyaltyBalance({ numberType: e.target.value })
                          )
                        }
                        onFocus={() => handleFocus('loyaltyBalance.numberType')}
                        mb={4}
                      >
                        <option value="integer">Integer</option>
                        <option value="decimal">Decimal</option>
                      </Select>
                      {errors['loyaltyBalance.numberType'] && (
                        <FormErrorMessage
                          pl={2}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                        >
                          <CustomErrorIcon />
                          {errors['loyaltyBalance.numberType']}
                        </FormErrorMessage>
                      )}
                      {errors['loyaltyBalance.enabled'] && (
                        <FormErrorMessage
                          pl={2}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                        >
                          <CustomErrorIcon />
                          {errors['loyaltyBalance.enabled']}
                        </FormErrorMessage>
                      )}
                    </FormControl>
                  </FieldEditIndicator>
                </Collapse>
              </Box>
            )}
          <Flex align="flex-start">
            {isDraggable && (
              <Flex
                {...dragHandleProps}
                cursor="grab"
                flexShrink={0}
                align="center"
                gap={3}
                mr={4}
                ml={-2}
                h={isLink ? '105px' : '62px'}
                mt={autoGenerateFieldId ? 6 : 5}
              >
                <DragDotsIcon w="7px" h="13px" color="#989898" />
                <Box h="100%" w="1px" bg="#989898" mt="5px" />
              </Flex>
            )}
            <Box flex={1}>
              {!autoGenerateFieldId && (
                <FieldEditIndicator
                  fieldRef={idFieldRef}
                  isFieldBeingEdited={isShared ? isFieldBeingEdited : false}
                  activeFieldRef={activeFieldRef}
                  left="-55px"
                  top="25px"
                >
                  <FormControl
                    mt={3}
                    isInvalid={!!errors[idPath] && isShared}
                    isDisabled={!isShared}
                  >
                    <AddonInput
                      label="ID"
                      placeholder="Enter an ID"
                      value={field.id || ''}
                      onChange={(value) => handleChange('id', value)}
                      onFocus={() => handleFocus(`${section}.${slot}.id`)}
                      isRequired={!!field.label || !!field.defaultValue}
                      compact={autoGenerateFieldId}
                    />
                    {errors[idPath] && isShared && (
                      <FormErrorMessage
                        pl={2}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                      >
                        <CustomErrorIcon />
                        {errors[idPath]}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </FieldEditIndicator>
              )}
              <FieldEditIndicator
                fieldRef={labelFieldRef}
                isFieldBeingEdited={isShared ? isFieldBeingEdited : false}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="25px"
              >
                <FormControl
                  mt={3}
                  isInvalid={!!errors[labelPath] && isShared}
                  isDisabled={!isShared}
                >
                  <AddonInput
                    label="Label"
                    placeholder={
                      isLoyaltyBalanceField
                        ? 'E.g. Balance or Points'
                        : 'Enter a label'
                    }
                    value={field.label || ''}
                    onChange={(value) => handleChange('label', value)}
                    onFocus={() => handleFocus(`${section}.${slot}.label`)}
                    compact={autoGenerateFieldId}
                  />
                  {errors[labelPath] && isShared && (
                    <FormErrorMessage
                      pl={2}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {errors[labelPath]}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>

              {!(
                templateType === 'stampCard' &&
                section === 'header' &&
                slot === 'one'
              ) && (
                <FieldEditIndicator
                  fieldRef={defaultValueFieldRef}
                  isFieldBeingEdited={isShared ? isFieldBeingEdited : false}
                  activeFieldRef={activeFieldRef}
                  left="-55px"
                  top="25px"
                >
                  {isLink ? (
                    (() => {
                      const parsed = parseAnchorTag(field.defaultValue ?? '');
                      return (
                        <>
                          <FormControl mt={3} isDisabled={!isShared}>
                            <AddonInput
                              label="Link text"
                              placeholder="Enter link text"
                              value={parsed.linkText}
                              onChange={(value) =>
                                handleLinkFieldChange('linkText', value)
                              }
                              onFocus={() =>
                                handleFocus(`${section}.${slot}.defaultValue`)
                              }
                              compact={autoGenerateFieldId}
                            />
                          </FormControl>
                          <FormControl
                            mt={3}
                            isInvalid={!!errors[defaultValuePath] && isShared}
                            isDisabled={!isShared}
                          >
                            <AddonInput
                              label="URL"
                              placeholder="Enter a URL"
                              value={parsed.linkUrl}
                              onChange={(value) =>
                                handleLinkFieldChange('linkUrl', value)
                              }
                              onFocus={() =>
                                handleFocus(`${section}.${slot}.defaultValue`)
                              }
                              compact={autoGenerateFieldId}
                            />
                            {errors[defaultValuePath] && isShared && (
                              <FormErrorMessage
                                pl={2}
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                              >
                                <CustomErrorIcon />
                                {errors[defaultValuePath]}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        </>
                      );
                    })()
                  ) : (
                    <FormControl
                      mt={3}
                      isInvalid={
                        (!!errors[defaultValuePath] && isShared) ||
                        hasNonNumericValue
                      }
                      isDisabled={!isShared}
                    >
                      <AddonInput
                        label="Default Value"
                        placeholder={
                          isLoyaltyBalanceField
                            ? loyaltyBalance.numberType === 'decimal'
                              ? '0.00'
                              : '0'
                            : 'Enter a default value'
                        }
                        value={
                          (field.defaultValue as string | number) === 0
                            ? '0'
                            : (field.defaultValue ?? '')
                        }
                        onChange={(value) => {
                          if (isLoyaltyBalanceField) {
                            const filtered =
                              loyaltyBalance.numberType === 'decimal'
                                ? value
                                    .replace(/[^0-9.]/g, '')
                                    .replace(/(\..*)\./g, '$1')
                                : value.replace(/[^0-9]/g, '');
                            handleChange('defaultValue', filtered);
                          } else {
                            handleChange('defaultValue', value);
                          }
                        }}
                        onFocus={() =>
                          handleFocus(`${section}.${slot}.defaultValue`)
                        }
                        onBlur={
                          isLoyaltyBalanceField &&
                          loyaltyBalance.numberType === 'decimal'
                            ? () => {
                                const current = String(
                                  field.defaultValue ?? ''
                                );
                                if (current === '') return;
                                const num = parseFloat(current);
                                if (!isNaN(num)) {
                                  handleChange('defaultValue', num.toFixed(2));
                                }
                              }
                            : undefined
                        }
                        compact={autoGenerateFieldId}
                      />
                      {hasNonNumericValue && (
                        <FormErrorMessage
                          pl={2}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                        >
                          <CustomErrorIcon />
                          {LOYALTY_BALANCE_ERROR_MESSAGE}
                        </FormErrorMessage>
                      )}
                      {errors[defaultValuePath] &&
                        isShared &&
                        !hasNonNumericValue && (
                          <FormErrorMessage
                            pl={2}
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                          >
                            <CustomErrorIcon />
                            {errors[defaultValuePath]}
                          </FormErrorMessage>
                        )}
                      {expressivePassRolloutEnabled &&
                        section === 'central' &&
                        templateType === 'generic' && (
                          <CharLimitWarning
                            value={String(field.defaultValue ?? '')}
                            limit={47}
                          />
                        )}
                    </FormControl>
                  )}
                  <OverrideFieldValueRadio
                    section={section}
                    slot={slot}
                    isVisible={isEdit && overrideFieldValueOpen}
                    value={overrideFieldValue}
                    onChange={setOverrideFieldValue}
                  />
                  {templateType === 'stampCard' &&
                    stampFeedbackTypes?.includes('updatePassText') &&
                    section !== 'header' &&
                    field.id && (
                      <FieldEditIndicator
                        fieldRef={isStampCardFieldRef}
                        isFieldBeingEdited={isFieldBeingEdited}
                        activeFieldRef={activeFieldRef}
                        left="-55px"
                        top="25px"
                      >
                        <Flex
                          direction="row"
                          justifyContent="space-between"
                          mt={4}
                          alignItems="center"
                          gap={3}
                        >
                          <Text fontSize="14px" lineHeight="28px">
                            Update this field as stamps are collected
                          </Text>
                          <Tooltip
                            label={
                              !field.id ||
                              (!!stampShortKey &&
                                !!stampLongKey &&
                                stampShortKey !== `${section}_${slot}` &&
                                stampLongKey !== `${section}_${slot}` &&
                                'You can enable updates on up to two fields. Disable another field to enable this one')
                            }
                            placement="top"
                            hasArrow
                            fontSize="12px"
                            bg="inkBlue"
                            color="white"
                            borderRadius="6px"
                            textAlign="center"
                            p="10px"
                            w="205px"
                          >
                            <Box>
                              <BuilderSwitch
                                isChecked={
                                  stampShortKey === `${section}_${slot}` ||
                                  stampLongKey === `${section}_${slot}`
                                }
                                isDisabled={
                                  !!stampShortKey &&
                                  !!stampLongKey &&
                                  stampShortKey !== `${section}_${slot}` &&
                                  stampLongKey !== `${section}_${slot}`
                                }
                                onChange={(e) => {
                                  const sectionSlot = `${section}_${slot}`;
                                  const isChecked = e.target.checked;
                                  if (isChecked) {
                                    if (!stampShortKey) {
                                      dispatch(
                                        setSimpleField({
                                          field: 'stampShortKey',
                                          value: sectionSlot,
                                        })
                                      );
                                    } else if (!stampLongKey) {
                                      dispatch(
                                        setSimpleField({
                                          field: 'stampLongKey',
                                          value: sectionSlot,
                                        })
                                      );
                                    }
                                  } else {
                                    if (stampShortKey === sectionSlot) {
                                      dispatch(
                                        setSimpleField({
                                          field: 'stampShortKey',
                                          value: '',
                                        })
                                      );
                                      dispatch(clearStampShortValues());
                                      dispatch(
                                        clearNamespaceErrors('stampShortValues')
                                      );
                                    } else if (stampLongKey === sectionSlot) {
                                      dispatch(
                                        setSimpleField({
                                          field: 'stampLongKey',
                                          value: '',
                                        })
                                      );
                                      dispatch(clearStampLongValues());
                                      dispatch(
                                        clearNamespaceErrors('stampLongValues')
                                      );
                                    }
                                  }
                                }}
                              />
                            </Box>
                          </Tooltip>
                        </Flex>
                      </FieldEditIndicator>
                    )}
                </FieldEditIndicator>
              )}
            </Box>
          </Flex>
          {autoGenerateFieldId &&
            !(isMinRequiredReached && isShared) &&
            isOpen && <Box mb={6} />}
          {/* Remove field link */}
          {isMinRequiredReached && isShared && (
            <Flex mt={4} alignItems="center" justify="flex-end" gap={2}>
              <Link
                color="alertRed"
                fontSize="12px"
                onClick={() => {
                  const sectionSlot = `${section}_${slot}`;
                  dispatch(removeField({ section, slot }));
                  dispatch(setFocusedField(null));
                  dispatch(clearError(`fields.${section}.${slot}.id`));
                  dispatch(clearError(`fields.${section}.${slot}.label`));
                  dispatch(
                    clearError(`fields.${section}.${slot}.defaultValue`)
                  );
                  if (sectionSlot === stampShortKey) {
                    dispatch(clearNamespaceErrors('stampShortValues'));
                  }
                  if (sectionSlot === stampLongKey) {
                    dispatch(clearNamespaceErrors('stampLongValues'));
                  }
                }}
              >
                Remove field
              </Link>
            </Flex>
          )}
        </Collapse>
      </FormControl>
    </Box>
  );
};

export default TemplateContentField;
